import { computed, shallowRef, type ComputedRef, type Ref } from 'vue';

/** A tab's stable identity (`id`), display `name`, and arbitrary typed metadata. */
export interface TabRegistration<T> {
  id: string;
  name: string;
  info: T;
}

/** A registration projected with its current selection state + a select action. */
export interface TabEntry<T> extends TabRegistration<T> {
  active: boolean;
  select: () => void;
}

export interface TabsRegistry<T> {
  /** Registered tabs in registration order, each with `active` + `select()`. */
  entries: ComputedRef<TabEntry<T>[]>;
  selectedId: Ref<string | undefined>;
  select: (id: string) => void;
  /** Register (or, for a known id, update-in-place) a tab; returns an unregister fn. */
  register: (reg: TabRegistration<T>) => () => void;
}

/**
 * Ordered tab registry + selection logic. Selection is never written here
 * directly: every change is signalled through `onSelect`, and the owner
 * (`TabsRoot`, or a test) is the sole writer of `selectedId`. `info` is held in
 * a `shallowRef` array so metadata objects (e.g. component definitions) are not
 * made deeply reactive.
 */
export function createTabsRegistry<T>(opts: {
  selectedId: Ref<string | undefined>;
  onSelect: (id: string | undefined) => void;
}): TabsRegistry<T> {
  const { selectedId, onSelect } = opts;
  const registrations = shallowRef<TabRegistration<T>[]>([]);

  function unregister(id: string): void {
    const next = registrations.value.filter((r) => r.id !== id);
    if (next.length === registrations.value.length) return;
    registrations.value = next;
    if (selectedId.value === id) onSelect(next[0]?.id);
  }

  function register(reg: TabRegistration<T>): () => void {
    const index = registrations.value.findIndex((r) => r.id === reg.id);
    if (index >= 0) {
      const next = registrations.value.slice();
      next[index] = reg;
      registrations.value = next;
    } else {
      registrations.value = [...registrations.value, reg];
    }
    // Auto-select the first registered tab when nothing is selected yet.
    if (selectedId.value === undefined) onSelect(reg.id);
    return () => unregister(reg.id);
  }

  function select(id: string): void {
    if (selectedId.value === id) return;
    if (!registrations.value.some((r) => r.id === id)) return;
    onSelect(id);
  }

  const entries = computed<TabEntry<T>[]>(() =>
    registrations.value.map((r) => ({
      ...r,
      active: r.id === selectedId.value,
      select: () => select(r.id),
    }))
  );

  return { entries, selectedId, select, register };
}
